import { Component, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { LanguageService } from '../../../../services/language.service';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { PharmacyListItem, PharmacyFilters } from '../../../../services/pharmacy/pharmacy.model';
import { LookupService } from '../../../../services/pharmacy/lookup.service';
import { Router, RouterLink } from '@angular/router';
import { RequestService } from '../../../../services/pharmacy/request.service';

import { CancelPharmacyDialog } from '../cancel-pharmacy-dialog/cancel-pharmacy-dialog';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { Header } from './header/header';
import { Filter } from './filter/filter';
import { PharmacyRequestStatus } from '../../../../pages/admin-dashboard/pharmacies/pharmacies.model';
import { finalize, forkJoin } from 'rxjs';
import { Loading } from '../../../../utils/loading/loading';
import { NoPharmacies } from '../no-pharmacies/no-pharmacies';

@Component({
  selector: 'app-pharmacies-list',
  imports: [FormsModule, DatePipe, CancelPharmacyDialog, Header, Filter, Loading, RouterLink, NoPharmacies],
  templateUrl: './pharmacies-list.html',
  styleUrl: './pharmacies-list.scss',
})
export class PharmaciesList implements OnInit {
  private languageService = inject(LanguageService);
  private pharmacyService = inject(PharmacyService);
  private lookupService = inject(LookupService);
  private requestService = inject(RequestService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  // PAGINATION
  currentPage = signal(0); // 0-based for backend
  totalPages = signal(0);
  readonly PAGE_SIZE = 25;
  loadingPharmacies = signal(false);
  loadingCancel = signal(false);
  errorCancel = signal<string>('');
  pharmacyRequestId = signal<string>('');
  errorPharmacies = signal<string>('');

  hasPharmacies = signal(false);
  openDropdownId = signal<number | null>(null);
  assigningManagerId = signal<number | null>(null);
  assigningManagerName = signal<string | null>(null);
  revokingManagerId = signal<number | null>(null);
  revokingManagerUserId = signal<number | null>(null);
  revokingManagerName = signal<string | null>(null);
  cancelingPharmacyId = signal<number | null>(null);
  cancelingPharmacyName = signal<string | null>(null);
  pharmacies = signal<PharmacyListItem[] | null>(null);
  statuses = signal<LookupVTO[]>([]);

  currentActions = signal<LookupVTO[]>([]);
  filterSubmitted = signal<boolean>(false);

  filters = signal<PharmacyFilters>({
    statusId: undefined,
    lastModifiedOnFrom: undefined,
    lastModifiedOnTo: undefined,
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
    pageSize: 50,
  });

  VIEW_PHARMACY_DETAILS = signal<LookupVTO>({
    id: 6,
    titleEn: 'View Pharmacy Details',
    titleAr: 'عرض تفاصيل الصيدلية',
  });
  activeDropdownId = signal<number | null>(null);
  modifyingPharmacyId = signal<number | null>(null);

  ngOnInit() {
    this.loadStatuses();
    this.loadPharmacies();
  }

  loadStatuses() {
    this.pharmacyService.getAllStatuses().subscribe((res) => {
      this.statuses.set(res.list);
    });
  }

  loadPharmacies() {
    this.loadingPharmacies.set(true);
    this.errorPharmacies.set('');

    this.pharmacyService
      .getAllPharmacies({
        ...this.filters(),
        pageNum: this.currentPage(),
        pageSize: this.PAGE_SIZE,
      })
      .pipe(finalize(() => this.loadingPharmacies.set(false)))
      .subscribe({
        next: (res) => {
          this.pharmacies.set(res.list);
          this.totalPages.set(Math.ceil(res.total / this.PAGE_SIZE));
        },
        error: (err) => {
          this.errorPharmacies.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ!' : err.messageEn || 'some error occured!',
          );
        },
      });
  }

  totalPagesArray() {
    return Array.from({ length: this.totalPages() }, (_, i) => i);
  }

  hasActiveFilters(): boolean {
    return Object.entries(this.filters).some(([key, value]) => {
      if (value == undefined) {
        return false;
      }
      if (key == 'orderDir') {
        return false;
      }
      if (key == 'orderBy') {
        return false;
      }
      return true;
    });
  }

  toggleSortOrder() {
    console.log('emitting...');
    this.filters().orderDir = this.filters().orderDir == 'ASC' ? 'DESC' : 'ASC';
    this.loadPharmacies();
  }

  getSortLabel(): string {
    const isAr = this.languageService.getCurrentLocale().includes('ar');
    if (this.filters().orderDir == 'DESC') {
      return isAr ? 'تنازلي' : 'DESC';
    } else {
      return isAr ? 'تصاعدي' : 'ASC';
    }
  }

  applyFilters(filterApplied: {
    lastModifiedOnFrom?: string | undefined;
    lastModifiedOnTo?: string | undefined;
    statusIds?: number[];
  }) {
    this.filterSubmitted.set(true);
    this.currentPage.set(0);
    this.filters.set({
      ...this.filters(),
      // pageNum: this.currentPage() - 1,
      // pageSize: this.itemsPerPage(),
      lastModifiedOnFrom: filterApplied.lastModifiedOnFrom,
      lastModifiedOnTo: filterApplied.lastModifiedOnTo,
      // to the backend, because it's accepting string comma separated ids
      statusId: filterApplied.statusIds?.join(',').toString(),
    });

    this.loadPharmacies();
  }

  resetFilters() {
    this.filterSubmitted.set(false);
    this.currentPage.set(0);
    this.filters.set({
      statusId: undefined,
      lastModifiedOnFrom: undefined,
      lastModifiedOnTo: undefined,
      orderBy: 'LAST_MODIFICATION_DATE',
      orderDir: 'DESC',
    });
    this.loadPharmacies();
  }

  getStatusClass(status: LookupVTO): string {
    const statusKey = status.titleEn.toLowerCase().replace(/\s/g, '');
    switch (statusKey) {
      case 'pending':
        return 'bg-[#FEF3C7] text-[#D97706]';
      case 'inprogress':
        return 'bg-[#DBEAFE] text-[#1D4ED8]';
      case 'accepted':
        return 'bg-[#D1FAE5] text-[#059669]';
      case 'rejected':
        return 'bg-[#FEE2E2] text-[#DC2626]';
      case 'expired':
        return 'bg-[#F3F4F6] text-[#374151]';
      case 'feedbackneeded':
        return 'bg-[#E0E7FF] text-[#4338CA]';
      case 'cancelled':
        return 'bg-[#F1F5F9] text-[#475569]';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  toggleDropdown(event: Event, id: number) {
    event.stopPropagation();
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      // this.currentActions.set(['View Pharmacy Details']);
      const pharmacyStatusId = this.pharmacies()?.filter((pharmacy) => pharmacy.id === id)[0].status.id;
      this.getActions(pharmacyStatusId!);
    }
  }

  closeDropdown() {
    this.openDropdownId.set(null);
  }

  getLocalizedTitle(item: LookupVTO): string | undefined {
    return this.languageService.getCurrentLocale().includes('ar') ? item.titleAr : item.titleEn;
  }

  getActions(statusId: number) {
    switch (statusId) {
      case PharmacyRequestStatus.Feedback:
        this.currentActions.set([
          { titleEn: 'Cancel', titleAr: 'إلغاء الطلب', id: 1 },
          this.VIEW_PHARMACY_DETAILS(),
          { titleEn: 'Edit Request', titleAr: 'تعديل الطلب', id: 2 },
        ]);
        return;

      case PharmacyRequestStatus.Pending:
        this.currentActions.set([{ titleEn: 'Cancel', titleAr: 'إلغاء الطلب', id: 1 }, this.VIEW_PHARMACY_DETAILS()]);
        return;

      case PharmacyRequestStatus.Accepted:
      case PharmacyRequestStatus.Expired:
      case PharmacyRequestStatus.Rejected:
      case PharmacyRequestStatus.Cancelled:
      case PharmacyRequestStatus.InProgress:
        this.currentActions.set([this.VIEW_PHARMACY_DETAILS()]);
        return;

      default:
        return [];
    }
  }

  handleAction(event: Event, actionId: number, pharmacy: PharmacyListItem) {
    event?.stopPropagation();

    if (actionId === 6) {
      this.router.navigate(['user', 'pharmacies', 'view', pharmacy.id]);
    } else if (actionId === 1) {
      this.cancelingPharmacyId.set(pharmacy.id);
      this.cancelRequest(pharmacy.id, pharmacy.nickName);
      this.pharmacyRequestId.set(pharmacy.requestId);
    } else if (actionId === 2) {
      this.router.navigate(['/user/pharmacies/edit', pharmacy.id]);
    }
  }
  // handleAction(action: LookupVTO, pharmacyId: number) {
  //   const pharmacy = this.pharmacies().find((p) => p.id == pharmacyId);
  //   if (!pharmacy) return;

  //   switch (action.titleEn) {
  //     case 'View Pharmacy Details':
  //       this.viewDetails(pharmacyId);
  //       break;
  //     case 'Edit Pharmacy':
  //       this.editPharmacy(pharmacyId);
  //       break;
  //     case 'Assign Manager':
  //       this.assignManager(pharmacyId, pharmacy.nickName);
  //       break;
  //     case 'Revoke Manager':
  //       if (pharmacy.assignedTo) {
  //         this.revokeManager(pharmacyId, pharmacy.assignedTo.id, pharmacy.assignedTo.fullName);
  //       }
  //       break;
  //     case 'Cancel Request':
  //     case 'Cancel Pharmacy':
  //       this.cancelRequest(pharmacyId, pharmacy.nickName);
  //       break;
  //     default:
  //       console.warn('Unknown action:', action);
  //       break;
  //   }
  //   this.closeDropdown();
  // }

  editPharmacy(id: number) {
    this.router.navigate(['/user/pharmacies/edit', id]);
  }

  viewDetails(id: number) {
    this.router.navigate(['user/pharmacies/view', id]);
  }

  assignManager(id: number, name: string) {
    this.assigningManagerName.set(name);
    this.assigningManagerId.set(id);
  }

  onManagerAssigned(managerId: number) {
    if (this.assigningManagerId()) {
      this.pharmacyService.assignManager(this.assigningManagerId()!, { managerId }).subscribe(() => {
        this.assigningManagerId.set(null);
        this.loadPharmacies();
      });
    }
  }

  closeAssignManagerDialog() {
    this.assigningManagerId.set(null);
    this.assigningManagerName.set(null);
  }

  revokeManager(id: number, managerId: number, managerName: string) {
    this.revokingManagerId.set(id);
    this.revokingManagerUserId.set(managerId);
    this.revokingManagerName.set(managerName);
  }

  onManagerRevoked() {
    const pharmacyId = this.revokingManagerId();
    const managerId = this.revokingManagerUserId();
    if (pharmacyId && managerId) {
      this.pharmacyService.revokeManager(pharmacyId, managerId).subscribe({
        next: () => {
          this.closeRevokeManagerDialog();
          this.loadPharmacies();
        },
      });
    }
  }

  closeRevokeManagerDialog() {
    this.revokingManagerId.set(null);
    this.revokingManagerUserId.set(null);
    this.revokingManagerName.set(null);
  }

  cancelRequest(id: number, name: string) {
    this.cancelingPharmacyId.set(id);
    this.cancelingPharmacyName.set(name);
  }

  closeCancelDialog() {
    this.cancelingPharmacyId.set(null);
    this.cancelingPharmacyName.set(null);
  }

  onRequestCancelled() {
    const id = this.cancelingPharmacyId();

    if (id) {
      this.loadingCancel.set(true);
      this.pharmacyService
        .cancelPharmacy(id)
        .pipe(
          finalize(() => {
            this.loadingCancel.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.closeCancelDialog();
            this.loadPharmacies();
          },
          error: (err) => {
            this.errorCancel.set(
              this.isArabic() ? err.messageAr || 'حدث بعض الخطأ!' : err.messageEn || 'some error occured!',
            );
          },
        });
    }
  }

  onNavigateToPharmacyRequestDetails(pharamcyId: number) {
    this.router.navigate(['/user/pharmacies/view', pharamcyId]);
  }

  goToPage(page: number) {
    if (page < 0 || page >= this.totalPages()) return;
    this.currentPage.set(page);
    this.loadPharmacies();
  }

  nextPage() {
    this.goToPage(this.currentPage() + 1);
  }
  prevPage() {
    this.goToPage(this.currentPage() - 1);
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }
  isEnglish() {
    return this.locale.startsWith('en');
  }
}
